import { useState, useEffect, useRef } from 'react'
import { Props_JobDetailsVideo } from "./JobDetailsVideo.props";
import { openConsentPreferences } from '../utils/CookieConsent'
import { useVideoConsent } from '../hooks/useVideoConsent'

type Video = 'youtube' | 'vimeo' | 'video' | null

const setAutoplay = (url: string) => {
    const hasAutoplay = url.includes('autoplay=1');
    if (!hasAutoplay) {
        // Handle YouTube watch URLs - convert to embed format
        const isYoutubeWatchUrl = url.includes('youtube') || url.includes('youtu.be') && !url.includes('embed');
        if (isYoutubeWatchUrl) {
            const videoIdMatch = url.match(/(?:v=|\/)([0-9A-Za-z_-]{11})(?:&|$)/);
            const videoId = videoIdMatch ? videoIdMatch[1] : null;
            if (videoId) {
                return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
            }
        }

        // Handle Vimeo URLs - always convert to player embed format
        if (url.includes("vimeo")) {
            const videoIdMatch = url.match(/vimeo\.com\/(?:.*\/)?(\d+)(?:[?#].*)?$/);
            const videoId = videoIdMatch ? videoIdMatch[1] : null;

            if (videoId) {
                const embedUrl = `https://player.vimeo.com/video/${videoId}?autoplay=1`;
                return embedUrl;
            }
        }

        // For other URLs, append autoplay parameter
        const separator = url.includes("?") ? "&" : "?";
        return `${url}${separator}autoplay=1`;
    }
    return url;
}

const getVideoMimeType = (url: string): string => {
    const extension = url.split('.').pop()?.toLowerCase()
    switch (extension) {
        case 'mp4':
            return 'video/mp4'
        case 'webm':
            return 'video/webm'
        case 'ogg':
        case 'ogv':
            return 'video/ogg'
        case 'mov':
            return 'video/quicktime'
        default:
            return 'video/mp4'
    }
}

const getVideoType = (url: string): Video => {
    if (!url) return null
    if (url.includes('youtube') || url.includes('youtu.be')) return 'youtube'
    if (url.includes('vimeo')) return 'vimeo'
    return 'video'
}

const JobDetailsVideo = (props: Props_JobDetailsVideo) => {
    const { video, transcriptionLink, heading } = props;

    const hasConsent = useVideoConsent();
    const [videoType, setVideoType] = useState<Video>(null)
    const [videoUrl, setVideoUrl] = useState<string | null>(null)
    const [videoThumbnail, setVideoThumbnail] = useState<string | null>(null)
    const videoRef = useRef<HTMLElement | null>(null)
    const [isPlaying, setIsPlaying] = useState(false);

    useEffect(() => {
        // Try to get video URL from DOM element first
        const videoWidget = document.querySelector('.page-job-details__job-information--video .attrax-job-information-widget__dynamic-field.videourl')
        const videoUrlElement = videoWidget?.querySelector('.attrax-job-information-widget__dynamic-field-value')
        const urlFromDom = videoUrlElement?.textContent?.trim() || null

        // Fall back to video.url.value if not found in DOM
        const url = urlFromDom || video?.url?.value || null
        const type = url ? getVideoType(url) : null

        setVideoUrl(url)
        setVideoType(type)
        if (video?.thumbnail?.value) {
            setVideoThumbnail(video.thumbnail.value)
        } else if (type == 'youtube') {
            const videoIdMatch = url?.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|watch\?v=))([0-9A-Za-z_-]{11})/);
            const videoId = videoIdMatch ? videoIdMatch[1] : null;
            if (videoId) {
                setVideoThumbnail(`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`)
            }
        }
    }, [video?.url?.value, video?.thumbnail?.value])

    const playVideo = () => {
        setIsPlaying(true);
        const snippet = videoRef.current
        requestAnimationFrame(() => {
            snippet?.classList.add('snippet-job-details-video--playing')
        })
    }

    if (!videoUrl) return null

    return (
        <section className={`snippet-job-details-video ${!videoUrl ? 'snippet-job-details-video--no-video' : ''}`} ref={videoRef}>
            <div className={`snippet-job-details-video__container`}>
                <h2 className={`snippet-job-details-video__heading ${heading.visibility.value ? `accessible-heading` : ''}`}>{heading.content.value}</h2>
                <div className={`snippet-job-details-video__container-video`}>
                    <img
                        className="snippet-job-details-video__thumbnail"
                        src={videoThumbnail || undefined}
                        alt={video?.alt?.value || ''}
                        loading="lazy"
                        width={384}
                        height={270}
                    />
                    {videoUrl && (
                        <>
                            {hasConsent && (
                                <button
                                    className="snippet-job-details-video__button-play"
                                    aria-label={`{{js.snippet.video.button.play.aria-label}}`}
                                    onClick={playVideo}
                                    data-cookie-play='play-with-cookies'
                                    disabled={!videoUrl}
                                >
                                    <span className="sr-only">{`{{js.snippet.video.button.play.hidden-label}}`}</span>
                                </button>
                            )}
                            {!hasConsent && (
                                <p className="snippet-job-details-video__consent-message">
                                    {`{{js.snippet.video.cookie.info}}`}{' '}
                                    <button onClick={openConsentPreferences}>
                                        {`{{js.snippet.video.cookie.info.trigger}}`}
                                    </button>
                                </p>
                            )}
                            {isPlaying && (videoType === 'youtube' || videoType === 'vimeo') ? (
                                <iframe
                                    className="snippet-job-details-video__video"
                                    src={isPlaying ? setAutoplay(videoUrl) : videoUrl}
                                    width={384}
                                    height={270}
                                    title="Video player"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    referrerPolicy={videoType === 'youtube' ? 'strict-origin-when-cross-origin' : undefined}
                                    allowFullScreen
                                ></iframe>
                            ) : isPlaying && videoType === 'video' && (
                                <video
                                    className="snippet-job-details-video__video"
                                    controls
                                    controlsList='nodownload'
                                    width={384}
                                    height={270}
                                    autoPlay
                                    tabIndex={0}
                                >
                                    <track kind="captions" />
                                    <source src={videoUrl} type={getVideoMimeType(videoUrl)} />
                                    Your browser does not support the video tag.
                                </video>
                            )}
                        </>
                    )}
                </div>

                {transcriptionLink?.linkText?.value && transcriptionLink?.linkHref?.value && (
                    <div className="snippet-job-details-video__link-container">
                        <a
                            href={transcriptionLink.linkHref.value}
                            className="snippet-job-details-video__transcription-link"
                            target={transcriptionLink.linkTarget?.value ? '_blank' : '_self'}
                            rel={transcriptionLink.linkTarget?.value ? 'noopener noreferrer' : undefined}
                        >
                            {transcriptionLink.linkText.value}
                        </a>
                    </div>
                )}
            </div>
        </section>
    )
}

export default JobDetailsVideo

